import { FC, useEffect, useState } from 'react';
import { Fade } from '@mui/material';
import classNames from 'classnames';

import NoWifi from '../../assets/img/no-wifi.svg';
import { useIsMobile, useMaintenanceMode } from '../../hooks';

import { MaintenanceMessage } from './MaintenanceNotice';

import styles from './MaintenanceNoticeBar.module.scss';

// Show the bar for 7 seconds, then hide it until the flag is toggled again.
const VISIBLE_DURATION_MS = 7000;

/**
 * Transient banner announcing that trading is suspended, shown for a few
 * seconds whenever the `maintenance` flag turns on.
 *
 * It is only an announcement - the persistent notice that replaces the
 * Buy/Sell buttons carries the message for the rest of the session, so this
 * deliberately gets out of the way.
 */
const MaintenanceNoticeBar: FC = (): JSX.Element | null => {
  const { isMobile } = useIsMobile();
  const isMaintenance = useMaintenanceMode();
  const [isVisible, setIsVisible] = useState(true);

  // Keyed on the flag so the bar re-announces if maintenance is turned off and
  // back on within the same session, instead of only firing once on mount.
  useEffect(() => {
    if (!isMaintenance) {
      return undefined;
    }

    setIsVisible(true);
    const timer = setTimeout(() => setIsVisible(false), VISIBLE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [isMaintenance]);

  if (!isMaintenance || !isVisible) {
    return null;
  }

  return (
    <Fade in>
      <div
        className={classNames(styles.container, {
          [styles.containerMob]: isMobile,
          [styles.containerWeb]: !isMobile,
        })}
      >
        <div className={styles.content}>
          <img src={NoWifi} className={styles.icon} alt="" />
          <span className={styles.message}><MaintenanceMessage /></span>
        </div>
      </div>
    </Fade>
  );
};

export default MaintenanceNoticeBar;
